import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { shortDate } from '@/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, ArrowUpDown, Plus, Download, CheckCircle2, XCircle, LogIn, Info, MoreHorizontal, Mail, AtSign } from 'lucide-react';
import { ChangeUserEmailDialog } from '@/components/ChangeUserEmailDialog';
import { UserEmailHistory } from '@/components/UserEmailHistory';
import { ChangeTikTokHandleDialog } from '@/components/ChangeTikTokHandleDialog';
import { getEmailChangeContext, getHandleChangeContext } from '@/lib/workerApi';
import { DataPagination } from '@/components/DataPagination';
import BrandUsers from '@/components/BrandUsers';
import AgencyUsers from '@/components/AgencyUsers';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import type { DateRangeValue } from '@/components/DateRangeFilter';
import { logUpdate } from '@/lib/audit-log';
import { startCreatorImpersonation } from '@/lib/impersonation';
import { TikTokStatusCell, TikTokConnectionCard, DisconnectTikTokDialog } from '@/components/CreatorTikTokConnection';

// Onboarding has no stored "current step" column on profiles — it's computed
// server-side by the creator_onboarding_progress VIEW (see
// creator-app/supabase/migrations/20260723000001_creator_onboarding_step_view.sql),
// which mirrors the resume order in creator-app/app/setup-profile.tsx:
//   tiktok_connect -> profile -> demographics -> categories -> language -> rates
// Filtering/paginating/counting against that view happens entirely in
// Postgres via applyCreatorFilters below — nothing is fetched-then-filtered
// in the browser.
const ONBOARDING_STEPS = [
  { value: '1', label: 'Step 1: TikTok Connect' },
  { value: '2', label: 'Step 2: Profile Setup' },
  { value: '3', label: 'Step 3: Demographics' },
  { value: '4', label: 'Step 4: Categories' },
  { value: '5', label: 'Step 5: Language Preferences' },
  { value: 'complete', label: 'Complete' },
] as const;

// The Coach tab tracks the BMC Coach portal's own setup instead
// (creator-app-web src/components/portal/CoachOnboarding.tsx). Derived the same
// way, by the creator_onboarding_progress.coach_onboarding_step column — see
// creator-app/supabase/migrations/20261013000000_admin_coach_onboarding_step.sql.
// NULL there means no Coach seat.
const COACH_ONBOARDING_STEPS = [
  { value: '1', label: 'Step 1: Secure Account' },
  { value: '2', label: 'Step 2: Choose TikTok' },
  { value: '3', label: 'Step 3: Connect Coach in Claude' },
  { value: 'complete', label: 'Complete' },
] as const;

type OnboardingStep = { value: string; label: string };

function stepLabel(value: string, steps: readonly OnboardingStep[] = ONBOARDING_STEPS): string {
  return steps.find(s => s.value === value)?.label ?? value;
}

/** Which creator list a CreatorUsers instance shows. 'coach' is every BMC
 *  Coach creator, whether or not they also use the creator app: anyone with a
 *  Coach seat (`coach_status`) or whose account the Coach purchase flow
 *  created (`signup_source = 'bmc_coach'`), shown with their COACH data (seat
 *  status, the TikTok they picked for Coach, Coach onboarding). 'creators' is
 *  every creator, Coach buyers included, shown with their APP profile. Both
 *  columns come from creator_onboarding_progress — see migration
 *  20260930120000_admin_creators_coach_flags.sql. */
type CreatorSegment = 'creators' | 'coach';

/** Narrows a creator_onboarding_progress query to the Coach tab. The Creators
 *  tab is every creator, so it is left as is. PostgREST ANDs separate `or`
 *  filters together, so this composes with the search filter. */
function applySegment(query: any, segment: CreatorSegment) {
  // A Coach seat whose buyer never made an app account comes through the view
  // as a synthetic row (has_app_account false, migration 20261027000000). It
  // belongs on the Coach tab only — the Creators tab is app creators.
  return segment === 'coach'
    ? query.or('coach_status.not.is.null,signup_source.eq.bmc_coach')
    : query.eq('has_app_account', true);
}

/** Narrows to onboarding complete/incomplete for the tab's own onboarding. The
 *  Creators tab keeps reading onboarding_complete exactly as before; a Coach
 *  account with no seat counts as incomplete. */
function applyOnboardingStatus(query: any, segment: CreatorSegment, complete: boolean) {
  if (segment === 'coach') {
    return complete
      ? query.eq('coach_onboarding_step', 'complete')
      : query.or('coach_onboarding_step.is.null,coach_onboarding_step.neq.complete');
  }
  return query.eq('onboarding_complete', complete);
}

// Shared by the paginated query and CSV export — every "creator list" fetch
// applies the same set of filters, all evaluated server-side by Postgres.
function applyCreatorFilters(query: any, opts: {
  search: string; proFilter: string; onboardingFilter: string; stepFilter: string; tiktokFilter: string;
  dateRange: DateRangeValue | null; segment: CreatorSegment;
}) {
  const { search, proFilter, onboardingFilter, stepFilter, tiktokFilter, dateRange, segment } = opts;
  query = applySegment(query, segment);
  if (search) {
    // The Coach tab searches the TikTok chosen for Coach, not the app's, and
    // also coach_email — the address the seat was bought with. That is the one
    // printed on the Whop receipt support is holding, and it is frequently not
    // the address the creator uses in the app.
    const extra = segment === 'coach'
      ? `,coach_tiktok_username.ilike.%${search}%,coach_email.ilike.%${search}%`
      : `,tiktok_handle.ilike.%${search}%`;
    query = query.or(`email.ilike.%${search}%,full_name.ilike.%${search}%${extra}`);
  }
  // `proFilter` is the Status select: Pro/Free (app) on Creators, Coach
  // subscription Active/Not active on the Coach tab.
  if (segment === 'coach') {
    if (proFilter === 'active') query = query.eq('coach_status', 'active');
    else if (proFilter === 'inactive') query = query.or('coach_status.is.null,coach_status.neq.active');
  } else if (proFilter === 'pro') query = query.eq('is_pro', true);
  else if (proFilter === 'free') query = query.eq('is_pro', false);
  if (onboardingFilter !== 'all') query = applyOnboardingStatus(query, segment, onboardingFilter === 'complete');
  if (stepFilter !== 'all') query = query.eq(segment === 'coach' ? 'coach_onboarding_step' : 'onboarding_step', stepFilter);
  // profiles.is_tiktok_connected — the flag the app and the Coach portal read.
  if (tiktokFilter !== 'all') query = query.eq('is_tiktok_connected', tiktokFilter === 'connected');
  if (dateRange) {
    query = query.gte('created_at', dateRange.from.toISOString()).lte('created_at', dateRange.to.toISOString());
  }
  return query;
}

// Same order for the list and the export. A Coach account with no seat has no
// Coach columns (NULL): keep those last on a descending sort, not first.
function sortOptions(field: string, ascending: boolean) {
  return field.startsWith('coach_') ? { ascending, nullsFirst: false } : { ascending };
}

/** The Coach seat's entitlement as badge text. NULL = no seat. */
function coachStatusLabel(status: string | null): string {
  if (!status) return 'No seat';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

// last_sign_in_at lives only in auth.users (not on profiles and not
// PostgREST-selectable) — fetch it for a page of rows via a SECURITY DEFINER
// RPC and merge it in. Mutates `rows` in place.
async function attachLastSignIn(rows: any[]) {
  const ids = rows.map((r: any) => r.id).filter(Boolean);
  if (ids.length === 0) return;
  const { data: signInRows } = await supabase.rpc('admin_get_last_sign_in', { p_user_ids: ids });
  const lastSignInById = new Map<string, string>(
    (signInRows ?? []).map((s: any) => [s.id, s.last_sign_in_at])
  );
  for (const r of rows) r.last_sign_in_at = lastSignInById.get(r.id) ?? null;
}

// Hidden Gems access (migration 20261027000004). profiles.hidden_gems_access
// is an admin override: TRUE granted, FALSE removed, NULL = the normal rules
// (creator-app hooks/useHiddenGemsAccess.ts). It isn't in
// creator_onboarding_progress (that view expands p.* at creation time), so it
// is read for the page's rows instead of rebuilding the view. A failed read
// never breaks the list: the switches just stay disabled. Mutates `rows`.
// is_new_member (20261029000000) rides along for the same reason.
async function attachHiddenGemsAccess(rows: any[]) {
  const ids = rows.filter((r: any) => r.has_app_account !== false).map((r: any) => r.id).filter(Boolean);
  if (ids.length === 0) return;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, hidden_gems_access, hidden_gems_access_reason, hidden_gems_access_set_at, is_new_member')
    .in('id', ids);
  if (error) { console.warn('Hidden Gems access not loaded:', error.message); return; }
  const byId = new Map<string, any>((data ?? []).map((p: any) => [p.id, p]));
  for (const r of rows) {
    const p = byId.get(r.id);
    if (!p) continue;
    r.hidden_gems_access = p.hidden_gems_access ?? null;
    r.hidden_gems_access_reason = p.hidden_gems_access_reason ?? null;
    r.hidden_gems_access_set_at = p.hidden_gems_access_set_at ?? null;
    r.is_new_member = p.is_new_member === true;
    r.hidden_gems_loaded = true;
  }
}

/** What gives this creator Hidden Gems everywhere under the normal rules, if
 *  anything. Same signals as the app (Coach seat not cancelled, is_pro, CAP).
 *  The CAP counts only for members from before 2026-10-29: later members get
 *  them outside the US only (isNewCapMemberOnly), so for the switch they're
 *  not "included" and turning it ON writes a grant that works in the US too. */
function hiddenGemsRuleSource(u: any): 'Coach' | 'Pro' | 'CAP' | null {
  if (u?.coach_status && u.coach_status !== 'cancelled') return 'Coach';
  if (u?.is_pro) return 'Pro';
  if (u?.is_cap_member && !u.is_new_member) return 'CAP';
  return null;
}

/** A CAP member who joined after 2026-10-29 and has nothing else: Hidden
 *  Gems outside the US storefront only. */
function isNewCapMemberOnly(u: any): boolean {
  return !!u?.is_cap_member && !!u.is_new_member && hiddenGemsRuleSource(u) === null;
}

/** Whether the creator has Hidden Gems: the admin override if set, else the rules. */
function hasHiddenGems(u: any): boolean {
  if (u?.hidden_gems_access === true) return true;
  if (u?.hidden_gems_access === false) return false;
  return hiddenGemsRuleSource(u) !== null;
}

/** Turning access ON needs a finished app onboarding, unless the creator has
 *  Coach (or is_pro, stamped on every Coach purchase). The RPC enforces the
 *  same rule. Turning it OFF is never blocked. */
function hiddenGemsOnBlocked(u: any): boolean {
  const hasCoach = !!u?.is_pro || (!!u?.coach_status && u.coach_status !== 'cancelled');
  return !hasCoach && !u?.onboarding_complete;
}

/** Short reason the switch is in its state, shown under it. */
function hiddenGemsSourceLabel(u: any): string {
  if (u?.hidden_gems_access === true) return 'Granted by admin';
  if (u?.hidden_gems_access === false) return 'Removed by admin';
  const rule = hiddenGemsRuleSource(u);
  if (rule) return `Included with ${rule}`;
  return isNewCapMemberOnly(u) ? 'CAP: outside the US only' : 'No access';
}

/** "Allow Hidden Gems access" switch. ON when the creator has access, whether
 *  from Coach/Pro/CAP or an admin grant. Stops the click reaching the row
 *  (which opens the detail sheet). Disabled only when there is no app account
 *  (nothing to store it on) or the value couldn't be loaded. */
function HiddenGemsSwitch({ user, onRequest, showSource = false }: {
  user: any;
  onRequest: (user: any, value: boolean) => void;
  showSource?: boolean;
}) {
  const checked = hasHiddenGems(user);
  // Only ever blocks switching ON, so an ON switch can always be turned off.
  const onboardingBlocked = !checked && hiddenGemsOnBlocked(user);
  const disabled = user?.has_app_account === false || !user?.hidden_gems_loaded || onboardingBlocked;
  const title = user?.has_app_account === false ? 'No app account'
    : !user?.hidden_gems_loaded ? 'Could not load'
    : onboardingBlocked ? 'App onboarding not finished. Hidden Gems can be turned on once it is, or if they have BMC Coach.'
    : hiddenGemsSourceLabel(user);
  return (
    <div onClick={(e) => e.stopPropagation()} title={title}>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onRequest(user, v)}
        aria-label="Allow Hidden Gems access"
      />
      {showSource && user?.hidden_gems_loaded && (
        <div className="mt-1 whitespace-nowrap text-[10px] text-muted-foreground">
          {onboardingBlocked ? 'Onboarding not finished' : hiddenGemsSourceLabel(user)}
        </div>
      )}
    </div>
  );
}

/** "Coach" tag for any creator with a BMC Coach seat, or whose account the
 *  Coach purchase flow created. */
/** A Coach seat with no app account behind it. The row is the seat, so the
 *  profile columns are empty and the detail sheet has nothing to open — say
 *  that plainly rather than letting it read as a broken account. */
function NoAppAccountBadge({ user }: { user: any }) {
  if (user?.has_app_account !== false) return null;
  return (
    <span
      title="Bought BMC Coach but never signed up in the app. There is no BMC account for this address, so the app columns are empty."
      className="ml-2 inline-flex items-center rounded-full border border-amber-200 bg-amber-100 px-1.5 py-0 text-[10px] font-semibold align-middle text-amber-800"
    >
      No app account
    </span>
  );
}

function CoachBadge({ user }: { user: any }) {
  if (!user?.coach_status && user?.signup_source !== 'bmc_coach') return null;
  const inactive = user.coach_status && user.coach_status !== 'active';
  return (
    <span
      title={user.coach_status ? `BMC Coach seat: ${user.coach_status}` : 'Account created by BMC Coach (no seat found)'}
      className={`ml-2 inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold align-middle ${inactive
        ? 'border-muted-foreground/30 bg-muted text-muted-foreground'
        : 'border-pink-200 bg-pink-100 text-pink-800'}`}
    >
      Coach{inactive ? ` · ${user.coach_status}` : ''}
    </span>
  );
}

/** Whether the creator has a TikTok username to change. */
const hasTikTokHandle = (u: { tiktok_handle?: string | null }) => !!u?.tiktok_handle?.trim();

function CreatorUsers({ segment = 'creators' }: { segment?: CreatorSegment }) {
  const isCoachTab = segment === 'coach';
  const onboardingSteps: readonly OnboardingStep[] = isCoachTab ? COACH_ONBOARDING_STEPS : ONBOARDING_STEPS;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [pendingProChange, setPendingProChange] = useState<boolean | null>(null);
  // Hidden Gems switch awaiting confirmation (from a row or the detail sheet).
  const [pendingGemsAccess, setPendingGemsAccess] = useState<{ user: any; value: boolean } | null>(null);
  const [gemsAccessReason, setGemsAccessReason] = useState('');
  const [gemModalUser, setGemModalUser] = useState<any | null>(null);
  // Support actions on a user. Held here rather than per row so the dialog
  // isn't remounted 25 times per page render.
  const [emailChangeUser, setEmailChangeUser] = useState<any | null>(null);
  const [handleChangeUser, setHandleChangeUser] = useState<any | null>(null);

  // Whether this admin holds users.change_email. Asked once per mount so the
  // menu never offers an action the worker would refuse. The worker and the DB
  // both re-check it — this is presentation only.
  const { data: handlePerms, error: handlePermsError } = useQuery({
    queryKey: ['admin-handle-change-context'],
    queryFn: getHandleChangeContext,
    staleTime: 5 * 60_000,
    retry: false,
    throwOnError: false,
  });

  const { data: supportPerms, error: supportPermsError } = useQuery({
    queryKey: ['admin-email-change-context'],
    queryFn: getEmailChangeContext,
    staleTime: 5 * 60_000,
    retry: false,
    // A worker/env misconfiguration must not break the Users page; the menu
    // just stays closed.
    throwOnError: false,
  });
  // Creator awaiting confirmation for "Log in as creator".
  const [loginAsUser, setLoginAsUser] = useState<any | null>(null);
  const [isLoggingInAs, setIsLoggingInAs] = useState(false);
  const [gemAmount, setGemAmount] = useState('');
  const [gemReason, setGemReason] = useState('');
  const [isAddingGems, setIsAddingGems] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [isExporting, setIsExporting] = useState(false);
  const [proFilter, setProFilter] = useState<string>('all');
  const [onboardingFilter, setOnboardingFilter] = useState<string>('all');
  const [stepFilter, setStepFilter] = useState<string>('all');
  const [tiktokFilter, setTiktokFilter] = useState<string>('all');
  // Creator awaiting confirmation for "Disconnect TikTok".
  const [tiktokDisconnectUser, setTiktokDisconnectUser] = useState<any | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(null);
  
  // Pagination state for detail sheet tabs
  // const [submissionsPage, setSubmissionsPage] = useState(1);
  // const [submissionsPageSize, setSubmissionsPageSize] = useState(10);
  // const [progressPage, setProgressPage] = useState(1);
  // const [progressPageSize, setProgressPageSize] = useState(10);
  const [gemsPage, setGemsPage] = useState(1);
  const [gemsPageSize, setGemsPageSize] = useState(10);
  
  console.log("🚀 ~ CreatorUsers ~ gemModalUser:", gemModalUser)

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', segment, sortField, sortAsc, currentPage, pageSize, search, proFilter, onboardingFilter, stepFilter, tiktokFilter, dateRange],
    queryFn: async () => {
      let query = applyCreatorFilters(
        supabase.from('creator_onboarding_progress').select('*', { count: 'exact' }),
        { search, proFilter, onboardingFilter, stepFilter, tiktokFilter, dateRange, segment }
      );
      query = query
        .order(sortField, sortOptions(sortField, sortAsc))
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      const { data, count, error } = await query;
      if (error) throw error;

      const rows = data ?? [];
      await Promise.all([attachLastSignIn(rows), attachHiddenGemsAccess(rows)]);
      return { data: rows, total: count ?? 0 };
    },
  });

  const users = usersData?.data ?? [];
  const totalUsers = usersData?.total ?? 0;
  const totalPages = Math.ceil(totalUsers / pageSize);

  // Read from the view (same role = 'creator' rows as profiles) so each tab
  // counts only its own creators. The Coach tab also counts live seats.
  const { data: onboardingStats } = useQuery({
    queryKey: ['creator-onboarding-stats', segment],
    queryFn: async () => {
      const base = () => applySegment(
        supabase.from('creator_onboarding_progress').select('id', { count: 'exact', head: true }),
        segment,
      );
      const [{ count: completeCount }, { count: incompleteCount }, active] = await Promise.all([
        applyOnboardingStatus(base(), segment, true),
        applyOnboardingStatus(base(), segment, false),
        isCoachTab ? base().eq('coach_status', 'active') : Promise.resolve({ count: 0 }),
      ]);
      return { completeCount: completeCount ?? 0, incompleteCount: incompleteCount ?? 0, activeSeats: active.count ?? 0 };
    },
    staleTime: 30_000,
  });
  const activeCoachSeats = onboardingStats?.activeSeats ?? 0;

  const onboardingCompleteCount   = onboardingStats?.completeCount   ?? 0;
  const onboardingIncompleteCount = onboardingStats?.incompleteCount ?? 0;

  // const { data: userSubmissionsData } = useQuery({
  //   queryKey: ['user-submissions', selectedUser?.id, submissionsPage, submissionsPageSize],
  //   enabled: !!selectedUser?.id,
  //   queryFn: async () => {
  //     const { data, count, error } = await supabase
  //       .from('challenge_submissions')
  //       .select('*', { count: 'exact' })
  //       .eq('user_id', selectedUser.id)
  //       .order('created_at', { ascending: false })
  //       .range((submissionsPage - 1) * submissionsPageSize, submissionsPage * submissionsPageSize - 1);
  //     if (error) throw error;
  //     return { data: data ?? [], total: count ?? 0 };
  //   },
  // });

  // const userSubmissions = userSubmissionsData?.data ?? [];
  // const totalSubmissions = userSubmissionsData?.total ?? 0;
  // const totalSubmissionsPages = Math.ceil(totalSubmissions / submissionsPageSize);

  // const { data: userProgressData } = useQuery({
  //   queryKey: ['user-progress', selectedUser?.id, progressPage, progressPageSize],
  //   enabled: !!selectedUser?.id,
  //   queryFn: async () => {
  //     const { data, count, error } = await supabase
  //       .from('user_challenge_progress')
  //       .select('*', { count: 'exact' })
  //       .eq('user_id', selectedUser.id)
  //       .range((progressPage - 1) * progressPageSize, progressPage * progressPageSize - 1);
  //     if (error) throw error;
  //     return { data: data ?? [], total: count ?? 0 };
  //   },
  // });

  // const userProgress = userProgressData?.data ?? [];
  // const totalProgress = userProgressData?.total ?? 0;
  // const totalProgressPages = Math.ceil(totalProgress / progressPageSize);

  const { data: userGemHistoryData } = useQuery({
    queryKey: ['user-gem-history', selectedUser?.id, gemsPage, gemsPageSize],
    enabled: !!selectedUser?.id,
    queryFn: async () => {
      const { data, count, error } = await supabase
        .from('credit_transactions')
        .select('*', { count: 'exact' })
        .eq('user_id', selectedUser.id)
        .order('created_at', { ascending: false })
        .range((gemsPage - 1) * gemsPageSize, gemsPage * gemsPageSize - 1);
      if (error) throw error;
      return { data: data ?? [], total: count ?? 0 };
    },
  });

  const userGemHistory = userGemHistoryData?.data ?? [];
  const totalGems = userGemHistoryData?.total ?? 0;
  const totalGemsPages = Math.ceil(totalGems / gemsPageSize);

  const handleAddGems = async () => {
    if (!gemModalUser || !gemAmount || Number(gemAmount) <= 0 || Number(gemAmount) > 100000) return;
    setIsAddingGems(true);
    try {
      const amount = Number(gemAmount);
      const currentBalance = gemModalUser.gem_count ?? gemModalUser.gems ?? 0;
      const newBalance = currentBalance + amount;
      
      const { error: updateError } = await supabase.rpc('admin_add_gems', {
        p_user_id: gemModalUser.id,
        p_amount: amount,
      });
      // if (updateError) throw updateError;
      const { error: logError } = await supabase.from('credit_transactions').insert({
        user_id: gemModalUser.id,
        amount,
        type: 'bonus',
        description: gemReason || null,
        created_at: new Date().toISOString(),
      });
      if (logError) console.error('Failed to log transaction:', logError);
      if (logError)  throw logError;

      await logUpdate('profiles', gemModalUser.id,
        { gem_count: currentBalance },
        { gem_count: newBalance },
        {
          description: `Admin granted ${amount} gems`,
          reason: gemReason || 'Manual admin grant',
          previousBalance: currentBalance,
          amountAdded: amount,
        }
      );
      toast.success(`✅ ${amount} gems added to @${gemModalUser.full_name || gemModalUser.tiktok_handle || gemModalUser.email}`);
      
      // Optimistically update cached users data without refetching
      queryClient.setQueryData(
        ['users', sortField, sortAsc, currentPage, pageSize, search, proFilter, onboardingFilter, stepFilter, dateRange],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((user: any) =>
              user.id === gemModalUser.id ? { ...user, gem_count: newBalance } : user
            ),
          };
        }
      );
      
      // Update gem history in modal
      queryClient.invalidateQueries({ queryKey: ['user-gem-history'] });
      
      setGemModalUser(null);
      setGemAmount('');
      setGemReason('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to add gems');
    } finally {
      setIsAddingGems(false);
    }
  };

  const toggleProMutation = useMutation({
    mutationFn: async ({ id, is_pro }: { id: string; is_pro: boolean }) => {
      const { data: oldData } = await supabase.from('profiles').select('is_pro').eq('id', id).single();
      const { error } = await supabase.rpc('admin_set_is_pro', { p_user_id: id, p_is_pro: is_pro });
      if (error) throw error;
      await logUpdate('profiles', id, { is_pro: oldData?.is_pro }, { is_pro }, {
        description: `${is_pro ? 'Enabled' : 'Disabled'} pro status`,
        action: 'toggle_pro_status',
      });
      return { id, is_pro };
    },
    onSuccess: (result) => {
      toast.success('Pro status updated');
      // Optimistically update cached users data without refetching
      queryClient.setQueryData(
        ['users', sortField, sortAsc, currentPage, pageSize, search, proFilter, onboardingFilter, stepFilter, dateRange],
        (old: any) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.map((user: any) =>
              user.id === result.id ? { ...user, is_pro: result.is_pro } : user
            ),
          };
        }
      );
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Switch ON:  rules already give it -> clear any removal (back to the rules);
  //             otherwise -> grant.
  // Switch OFF: rules give it -> remove; otherwise -> take back the grant.
  // OFF always needs a reason (enforced again by the RPC).
  const hiddenGemsAccessMutation = useMutation({
    mutationFn: async ({ user, value, reason }: { user: any; value: boolean; reason: string }) => {
      const byRules = hiddenGemsRuleSource(user) !== null;
      const access: boolean | null = value ? (byRules ? null : true) : (byRules ? false : null);
      const desc = value ? 'Allowed Hidden Gems access' : 'Turned Hidden Gems access off';
      const { data, error } = await supabase.rpc('admin_set_hidden_gems_access', {
        p_user_id: user.id,
        p_access: access,
        p_reason: reason.trim() || null,
        p_switch_off: !value,
      });
      if (error) {
        await logUpdate('profiles', user.id, { hidden_gems_access: user.hidden_gems_access ?? null }, { hidden_gems_access: access }, {
          description: `Failed: ${desc}`,
          action: 'set_hidden_gems_access',
          reason: reason.trim() || null,
          status: 'failed',
          error: error.message,
        });
        throw error;
      }
      await logUpdate('profiles', user.id, { hidden_gems_access: (data as any)?.previous ?? null }, { hidden_gems_access: access }, {
        description: desc,
        action: 'set_hidden_gems_access',
        reason: reason.trim() || null,
        rule_source: hiddenGemsRuleSource(user),
      });
      return { id: user.id, patch: { hidden_gems_access: access, hidden_gems_access_reason: reason.trim() || null, hidden_gems_access_set_at: new Date().toISOString() }, value };
    },
    onSuccess: ({ id, patch, value }) => {
      toast.success(value ? 'Hidden Gems access allowed' : 'Hidden Gems access turned off');
      // Every cached page of this tab, whatever its filters.
      queryClient.setQueriesData({ queryKey: ['users', segment] }, (old: any) => old && {
        ...old,
        data: old.data.map((u: any) => (u.id === id ? { ...u, ...patch } : u)),
      });
      setSelectedUser((sel: any) => (sel?.id === id ? { ...sel, ...patch } : sel));
    },
    onError: (err: any) => toast.error(err.message || 'Could not update Hidden Gems access'),
  });

  const requestHiddenGemsChange = (user: any, value: boolean) => {
    setGemsAccessReason('');
    setPendingGemsAccess({ user, value });
  };

  const toggleSort = (field: string) => {
    setCurrentPage(1);
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handlePageSizeChange = (newSize: number) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  const SortHeader = ({ field, children }: { field: string; children: React.ReactNode }) => (
    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
      <div className="flex items-center gap-1">{children}<ArrowUpDown className="h-3 w-3 shrink-0" /></div>
    </TableHead>
  );

  // Opens the creator web portal signed in as this creator, in a new tab. The
  // worker mints a one-time link (see lib/impersonation.ts startCreatorImpersonation).
  const handleLoginAs = async () => {
    const u = loginAsUser;
    if (!u) return;
    setIsLoggingInAs(true);
    try {
      const email = await startCreatorImpersonation(u.id, u.full_name);
      toast.success(`Opening the creator portal as ${u.full_name || email}…`);
      setLoginAsUser(null);
    } catch (err: any) {
      toast.error(err?.message || 'Could not log in as this creator');
    } finally {
      setIsLoggingInAs(false);
    }
  };

  const handleExportUsers = async () => {
    setIsExporting(true);
    try {
      let query = applyCreatorFilters(supabase.from('creator_onboarding_progress').select('*'), {
        search, proFilter, onboardingFilter, stepFilter, tiktokFilter, dateRange, segment,
      });
      query = query.order(sortField, sortOptions(sortField, sortAsc));

      const { data, error } = await query;
      if (error) throw error;

      const filteredData = (data ?? []).map((row: any) => ({
        ...row,
        onboarding_step: stepLabel(row.onboarding_step),
        ...(isCoachTab && {
          coach_onboarding_step: row.coach_onboarding_step == null ? 'No Coach seat' : stepLabel(row.coach_onboarding_step, COACH_ONBOARDING_STEPS),
          // Searching Whop by email fails whenever the buyer's Whop account
          // uses a different address, which is common. The membership id is
          // the one key that always finds them.
          coach_whop_membership_id: row.coach_whop_membership_id ?? '',
        }),
      }));

      if (filteredData.length === 0) {
        toast.error('No users available to export');
        return;
      }

      const columns: string[] = Array.from(
        filteredData.reduce((keys, row) => {
          Object.keys(row).forEach((key) => keys.add(key));
          return keys;
        }, new Set<string>())
      );

      const escapeCsvValue = (value: any) => {
        const stringValue = value == null
          ? ''
          : typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const csvRows = [
        columns.join(','),
        ...filteredData.map((row) => columns.map((column) => escapeCsvValue(row[column])).join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${isCoachTab ? 'coach-creators' : 'creator-users'}-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${filteredData.length} users`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to export users');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={`grid gap-4 ${isCoachTab ? 'grid-cols-2 sm:grid-cols-3 sm:max-w-2xl' : 'grid-cols-2 sm:max-w-md'}`}>
        {isCoachTab && (
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <p className="text-xs font-semibold uppercase tracking-wide">Active Coach seats</p>
              </div>
              <p className="text-2xl font-bold">{activeCoachSeats}</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <p className="text-xs font-semibold uppercase tracking-wide">Onboarding Complete</p>
            </div>
            <p className="text-2xl font-bold">{onboardingCompleteCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <XCircle className="h-3.5 w-3.5" />
              <p className="text-xs font-semibold uppercase tracking-wide">Onboarding Incomplete</p>
            </div>
            <p className="text-2xl font-bold">{onboardingIncompleteCount}</p>
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by email or name..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-9" />
          </div>
          <Select value={proFilter} onValueChange={(v) => { setCurrentPage(1); setProFilter(v); }}>
            <SelectTrigger className={isCoachTab ? 'w-44' : 'w-36'}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{isCoachTab ? 'All Coach Status' : 'All Users'}</SelectItem>
              {isCoachTab ? (
                <>
                  <SelectItem value="active">Coach Active</SelectItem>
                  <SelectItem value="inactive">Coach Not Active</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="pro">Pro Only</SelectItem>
                  <SelectItem value="free">Free Only</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          <Select value={onboardingFilter} onValueChange={(v) => { setCurrentPage(1); setOnboardingFilter(v); }}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Onboarding</SelectItem>
              <SelectItem value="complete">Onboarding Complete</SelectItem>
              <SelectItem value="incomplete">Onboarding Incomplete</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stepFilter} onValueChange={(v) => { setCurrentPage(1); setStepFilter(v); }}>
            <SelectTrigger className="w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Steps</SelectItem>
              {onboardingSteps.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={tiktokFilter} onValueChange={(v) => { setCurrentPage(1); setTiktokFilter(v); }}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All TikTok</SelectItem>
              <SelectItem value="connected">TikTok Connected</SelectItem>
              <SelectItem value="not_connected">TikTok Not Connected</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            value={dateRange}
            onChange={(r) => { setCurrentPage(1); setDateRange(r); }}
            placeholder="Select Date Range"
          />
        </div>
        <Button variant="outline" onClick={handleExportUsers} disabled={isExporting || !users || users?.length === 0}>
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </div>
      <Card>
        <CardContent>
          {/* One scroll container for both axes, so the horizontal scrollbar
              stays pinned at the bottom of the visible area. Cells are nowrap
              by default (see ui/table.tsx).
              Below sm the cap is lifted: a phone draws overlay scrollbars, so
              a nested vertical scroller paints its bar on top of the rightmost
              cells. The page owns vertical scrolling there and the table keeps
              only the horizontal axis. */}
          <Table containerClassName="max-h-[600px] max-sm:max-h-none">
              <TableHeader className="sticky top-0 z-10 bg-card [&_tr]:border-b-0 [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
                <TableRow>
                  <SortHeader field="email">Email</SortHeader>
                  <SortHeader field="full_name">Name</SortHeader>
                  <SortHeader field={isCoachTab ? 'coach_tiktok_username' : 'tiktok_handle'}>Username</SortHeader>
                  <SortHeader field="created_at">Joined</SortHeader>
                  <TableHead>Last seen</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>TikTok</TableHead>
                  <TableHead className="whitespace-nowrap" title="ON = has Hidden Gems in the creator app (via Coach, Pro, CAP or an admin grant). Admins can turn it on or off for anyone with an app account.">Hidden Gems</TableHead>
                  {/* One Onboarding column, not two: "Complete" made the second
                      column say "Complete" as well, and an incomplete user's
                      step reads fine underneath the badge. */}
                  <TableHead>{isCoachTab ? 'Coach Onboarding' : 'Onboarding'}</TableHead>
                  {/* COIN SYSTEM DISABLED: <SortHeader field="gem_count">Gems</SortHeader> */}
                  <SortHeader field="creator_gmv">GMV</SortHeader>
                  {/* Coach tools Claude has called for the seat — see
                      migration 20261014000001_admin_coach_mcp_calls.sql. */}
                  {isCoachTab && (
                    <SortHeader field="coach_mcp_calls">
                      <span className="whitespace-nowrap">MCP calls</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="How MCP calls are counted"
                            className="inline-flex shrink-0 text-muted-foreground hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Info className="h-3.5 w-3.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs space-y-1.5 text-xs font-normal normal-case leading-relaxed">
                          <p>How many times Claude has used one of this creator’s Coach tools.</p>
                          <p>Each tool Claude runs is one call. One question can be 0, 1 or several calls, so this measures how much Coach gets used, not how many questions were asked.</p>
                          <p>Failed calls count too. Opening a chat with the Coach doesn’t. Counted since Jul 8, 2026, and a call whose log write failed is missing, so the number can run slightly low.</p>
                          <p>“–” means no Coach seat.</p>
                        </TooltipContent>
                      </Tooltip>
                    </SortHeader>
                  )}
                  <TableHead>Activity</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isCoachTab ? 13 : 12} className="h-24 text-center text-muted-foreground">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : !users || users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isCoachTab ? 13 : 12} className="h-24 text-center text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users?.map((u: any) => (
                  <TableRow key={u.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedUser(u)}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell className="whitespace-nowrap">{u.full_name || '–'}<CoachBadge user={u} /><NoAppAccountBadge user={u} /></TableCell>
                    {/* The Coach tab shows the TikTok chosen for Coach, never the
                        app profile's — they can differ. */}
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {isCoachTab
                        ? (u.coach_tiktok_username ? `@${u.coach_tiktok_username}` : u.coach_status ? 'Not chosen yet' : '–')
                        : (u.tiktok_handle ? `@${u.tiktok_handle}` : '–')}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{shortDate(u.created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {shortDate(u.last_sign_in_at)}
                    </TableCell>
                    <TableCell>
                      {isCoachTab ? (
                        <>
                          <Badge variant={u.coach_status === 'active' ? 'default' : 'secondary'}>{coachStatusLabel(u.coach_status)}</Badge>
                          {u.coach_status === 'active' && u.coach_cancel_at_period_end && (
                            <div className="mt-1 text-xs text-muted-foreground">Cancels at period end</div>
                          )}
                          {u.coach_whop_membership_id && (
                            <div
                              className="mt-1 select-all font-mono text-[10px] text-muted-foreground"
                              title="Whop membership id — search this in Whop. Looking the buyer up by email there often fails, because their Whop account may use a different address."
                              onClick={e => e.stopPropagation()}
                            >
                              {u.coach_whop_membership_id}
                            </div>
                          )}
                        </>
                      ) : (
                        <Badge variant={u.is_pro ? 'default' : 'secondary'}>{u.is_pro ? 'Pro' : 'Free'}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <TikTokStatusCell connected={u.is_tiktok_connected === true} onDisconnect={() => setTiktokDisconnectUser(u)} />
                    </TableCell>
                    <TableCell>
                      <HiddenGemsSwitch user={u} onRequest={requestHiddenGemsChange} showSource />
                    </TableCell>
                    <TableCell>
                      {/* The tab's own onboarding: the app's on Creators, the
                          Coach portal's on Coach creators. */}
                      {(() => {
                        const step = isCoachTab ? u.coach_onboarding_step : u.onboarding_step;
                        const complete = isCoachTab ? step === 'complete' : u.onboarding_complete;
                        return (
                          <>
                            <Badge variant={complete ? 'default' : 'outline'}>
                              {complete ? 'Complete' : 'Incomplete'}
                            </Badge>
                            {!complete && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {step == null && isCoachTab ? 'No Coach seat' : stepLabel(step, onboardingSteps)}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    {/* COIN SYSTEM DISABLED: <TableCell className="whitespace-nowrap">{u.gem_count ?? u.gems ?? 0}</TableCell> */}
                    <TableCell className="whitespace-nowrap">
                      {u.creator_gmv == null
                        ? '–'
                        : Number(u.creator_gmv) === 0 || Number(u.creator_gmv) === 2500
                          ? '$0-$5k'
                          : `$${Number(u.creator_gmv).toLocaleString()}`}
                    </TableCell>
                    {isCoachTab && (
                      <TableCell className="whitespace-nowrap" title={u.coach_mcp_calls == null ? 'No Coach seat' : undefined}>
                        {u.coach_mcp_calls == null ? '–' : u.coach_mcp_calls.toLocaleString()}
                      </TableCell>
                    )}
                    <TableCell>
                      {u.roster_creator_id ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); navigate(`/roster-creators/${u.roster_creator_id}`); }}
                        >
                          View Activity
                        </Button>
                      ) : (
                        <span className="text-muted-foreground text-sm">–</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        {/* COIN SYSTEM DISABLED (add-gems button):
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setGemModalUser(u); }}>
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                        */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          title="Log in as creator"
                          onClick={(e) => { e.stopPropagation(); setLoginAsUser(u); }}
                        >
                          <LogIn className="h-3.5 w-3.5" />
                        </Button>
                        {/* Support actions. One menu rather than a column per
                            action — these are rare, deliberate operations, and
                            a row of buttons invites misclicks on the ones that
                            cannot be undone. */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                            <DropdownMenuLabel>Support actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              disabled={!supportPerms?.can_change_email}
                              onSelect={() => setEmailChangeUser(u)}
                            >
                              <Mail className="mr-2 h-3.5 w-3.5" />
                              Change sign-in email...
                            </DropdownMenuItem>
                            {/* Only offered when there is a username to change — a
                                creator without one sets it themselves in the app
                                (the worker refuses it too). */}
                            {hasTikTokHandle(u) && (
                              <DropdownMenuItem
                                disabled={!handlePerms?.can_change_handle}
                                onSelect={() => setHandleChangeUser(u)}
                              >
                                <AtSign className="mr-2 h-3.5 w-3.5" />
                                Change TikTok username...
                              </DropdownMenuItem>
                            )}
                            {(!supportPerms?.can_change_email || (hasTikTokHandle(u) && !handlePerms?.can_change_handle)) && (
                              <div className="max-w-[260px] px-2 py-1.5 text-[11px] text-muted-foreground">
                                {/* Three different causes produced the same
                                    "needs permission" line before, which sent
                                    people looking for a grant when the real
                                    problem was a worker that never answered. */}
                                {supportPermsError || handlePermsError
                                  ? 'Could not check permissions — the admin worker did not respond. Check VITE_WORKER_URL and that the worker is deployed.'
                                  : supportPerms?.is_auth_admin === false || handlePerms?.is_auth_admin === false
                                    ? 'Your account is not flagged as an admin (app_metadata.is_admin).'
                                    : 'Greyed-out actions need their permission granted in admin_users.'}
                              </div>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>

                      </div>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
          </Table>
          {users && users.length > 0 && (
            <DataPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalUsers}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!loginAsUser} onOpenChange={(open) => { if (!open && !isLoggingInAs) setLoginAsUser(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Log in as creator?</AlertDialogTitle>
            <AlertDialogDescription>
              This opens the creator web portal in a new tab, signed in as{' '}
              <strong>{loginAsUser?.full_name || loginAsUser?.email}</strong>. Anything you do there is done as them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoggingInAs}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isLoggingInAs}
              onClick={(e) => { e.preventDefault(); handleLoginAs(); }}
            >
              {isLoggingInAs ? 'Opening…' : 'Log in'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DisconnectTikTokDialog
        creator={tiktokDisconnectUser}
        onClose={() => setTiktokDisconnectUser(null)}
        onDisconnected={(id) => setSelectedUser((s: any) => (s?.id === id ? { ...s, is_tiktok_connected: false } : s))}
      />

      <ChangeUserEmailDialog
        user={emailChangeUser}
        open={!!emailChangeUser}
        onOpenChange={(open) => { if (!open) setEmailChangeUser(null); }}
      />

      <ChangeTikTokHandleDialog
        user={handleChangeUser}
        open={!!handleChangeUser}
        onOpenChange={(open) => { if (!open) setHandleChangeUser(null); }}
      />

      {/* Gem Adjustment Modal — COIN SYSTEM DISABLED
      <Dialog open={!!gemModalUser} onOpenChange={(open) => { if (!open) { setGemModalUser(null); setGemAmount(''); setGemReason(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Gems</DialogTitle></DialogHeader>
          {gemModalUser && (
            <div className="space-y-4">
              <div className="space-y-1 text-sm">
                <p className="font-medium">{gemModalUser.full_name || gemModalUser.tiktok_handle || '–'}</p>
                <p className="text-muted-foreground">{gemModalUser.email}</p>
                <p>Current balance: <span className="font-semibold">{gemModalUser.gem_count ?? gemModalUser.gems ?? 0} gems</span></p>
              </div>
              <div className="space-y-2">
                <Label>Gems to add</Label>
                <Input type="number" min="1" max="10000" placeholder="e.g. 50" value={gemAmount} onChange={(e) => setGemAmount(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Reason (internal note)</Label>
                <Textarea placeholder="e.g. Beta tester bonus, Support comp" value={gemReason} onChange={(e) => setGemReason(e.target.value)} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setGemModalUser(null); setGemAmount(''); setGemReason(''); }}>Cancel</Button>
            <Button onClick={handleAddGems} disabled={isAddingGems || !gemAmount || Number(gemAmount) <= 0 || Number(gemAmount) > 10000} className="bg-pink-600 hover:bg-pink-700 text-white">
              {isAddingGems ? 'Adding...' : 'Add Gems'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      */}

      <AlertDialog open={pendingGemsAccess !== null} onOpenChange={(open) => { if (!open) setPendingGemsAccess(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingGemsAccess?.value ? 'Allow Hidden Gems access' : 'Turn off Hidden Gems access'}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingGemsAccess?.value ? (
                <>Unlock all Hidden Gems in the creator app for <strong>{pendingGemsAccess?.user?.full_name || pendingGemsAccess?.user?.tiktok_handle || pendingGemsAccess?.user?.email}</strong>?</>
              ) : (
                <>
                  Turn off Hidden Gems in the creator app for <strong>{pendingGemsAccess?.user?.full_name || pendingGemsAccess?.user?.tiktok_handle || pendingGemsAccess?.user?.email}</strong>?
                  {hiddenGemsRuleSource(pendingGemsAccess?.user) && (
                    <> They currently have it with {hiddenGemsRuleSource(pendingGemsAccess?.user)}; this removes it anyway.</>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingGemsAccess && !pendingGemsAccess.value && (
            <div className="space-y-1.5">
              <Label htmlFor="gems-access-reason">Reason (required)</Label>
              <Textarea
                id="gems-access-reason"
                value={gemsAccessReason}
                onChange={(e) => setGemsAccessReason(e.target.value)}
                placeholder="Why is Hidden Gems access being turned off?"
                maxLength={500}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">At least 5 characters. Saved on the creator and in the audit log.</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!!pendingGemsAccess && !pendingGemsAccess.value && gemsAccessReason.trim().length < 5}
              onClick={() => {
                if (pendingGemsAccess) hiddenGemsAccessMutation.mutate({ user: pendingGemsAccess.user, value: pendingGemsAccess.value, reason: gemsAccessReason });
                setPendingGemsAccess(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Detail Drawer */}
      <Sheet open={!!selectedUser} onOpenChange={(open) => {
        if (!open) {
          setSelectedUser(null);
          // Reset pagination when closing
          // setSubmissionsPage(1);
          // setProgressPage(1);
          setGemsPage(1);
        }
      }}>
        <SheetContent className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader><SheetTitle>User Details</SheetTitle></SheetHeader>
          {selectedUser && (
            <div className="space-y-6 mt-6">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{selectedUser.email}</p>
              </div>
              {(selectedUser.coach_status || selectedUser.signup_source === 'bmc_coach') && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">BMC Coach</p>
                  <p className="font-medium">
                    {selectedUser.coach_status ? `Seat ${selectedUser.coach_status}` : 'No seat found'}
                    {selectedUser.coach_status === 'active' && selectedUser.coach_cancel_at_period_end && ' · cancels at period end'}
                    <CoachBadge user={selectedUser} />
                  </p>
                  {selectedUser.signup_source === 'bmc_coach' && (
                    <p className="text-xs text-muted-foreground">
                      Account created by the Coach purchase flow{selectedUser.onboarding_complete ? ', later set up in the creator app.' : '. Not set up in the creator app yet.'}
                    </p>
                  )}
                </div>
              )}
              {(isCoachTab ? selectedUser.coach_tiktok_username : selectedUser.tiktok_handle) && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{isCoachTab ? 'Coach username' : 'Username'}</p>
                  <p className="font-medium">@{isCoachTab ? selectedUser.coach_tiktok_username : selectedUser.tiktok_handle}</p>
                </div>
              )}
              <TikTokConnectionCard userId={selectedUser.id} onDisconnect={() => setTiktokDisconnectUser(selectedUser)} />
              <div className="flex items-center gap-3">
                <Switch
                  checked={selectedUser.is_pro}
                  onCheckedChange={(v) => setPendingProChange(v)}
                />
                <Label>{isCoachTab ? 'App Pro Status' : 'Pro Status'}</Label>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">CAP member</p>
                <p className="font-medium">
                  {selectedUser.is_cap_member ? (
                    <>
                      <Badge variant="default">CAP member</Badge>
                      {selectedUser.cap_joined_at && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">since {shortDate(selectedUser.cap_joined_at)}</span>
                      )}
                    </>
                  ) : (
                    <Badge variant="secondary">Not a CAP member</Badge>
                  )}
                </p>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-3">
                  <HiddenGemsSwitch user={selectedUser} onRequest={requestHiddenGemsChange} />
                  <Label>Allow Hidden Gems access</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  {hiddenGemsSourceLabel(selectedUser)}
                  {selectedUser.hidden_gems_access !== null && selectedUser.hidden_gems_access !== undefined && selectedUser.hidden_gems_access_set_at && (
                    <> · {shortDate(selectedUser.hidden_gems_access_set_at)}</>
                  )}
                  {selectedUser.hidden_gems_access === false && hiddenGemsRuleSource(selectedUser) && (
                    <> (would otherwise be included with {hiddenGemsRuleSource(selectedUser)})</>
                  )}
                </p>
                {selectedUser.hidden_gems_access_reason && (
                  <p className="text-xs text-muted-foreground">Reason: {selectedUser.hidden_gems_access_reason}</p>
                )}
                {!hasHiddenGems(selectedUser) && hiddenGemsOnBlocked(selectedUser) && (
                  <p className="text-xs text-amber-700">
                    App onboarding isn't finished, so Hidden Gems can't be turned on yet. Creators with BMC Coach are exempt.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Applies to the creator app. The BMC Coach portal follows the Coach plan.
                </p>
              </div>

              <AlertDialog open={pendingProChange !== null} onOpenChange={(open) => { if (!open) setPendingProChange(null); }}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Change Pro Status</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to {pendingProChange ? 'grant' : 'revoke'} Pro status for <strong>{selectedUser?.full_name || selectedUser?.tiktok_handle || selectedUser?.email}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => {
                      setSelectedUser({ ...selectedUser, is_pro: pendingProChange });
                      toggleProMutation.mutate({ id: selectedUser.id, is_pro: pendingProChange });
                      setPendingProChange(null);
                    }}>
                      Confirm
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Tabs defaultValue="email">
                <TabsList>
                  {/* <TabsTrigger value="submissions">Submissions</TabsTrigger>
                  <TabsTrigger value="progress">Progress</TabsTrigger> */}
                  {/* COIN SYSTEM DISABLED: <TabsTrigger value="gems">Gem History</TabsTrigger> */}
                  <TabsTrigger value="email">Email History</TabsTrigger>
                  <TabsTrigger value="handle">Username History</TabsTrigger>
                </TabsList>
                {/* <TabsContent value="submissions" className="space-y-2">
                  {userSubmissions?.length === 0 && <p className="text-sm text-muted-foreground">No submissions</p>}
                  {userSubmissions?.map((s: any) => (
                    <div key={s.id} className="border rounded-md p-3 text-sm">
                      <div className="flex justify-between">
                        <span>Day {s.challenge_day ?? s.day_number}</span>
                        <span className="text-muted-foreground">{s.created_at && format(new Date(s.created_at), 'MMM d')}</span>
                      </div>
                      {s.flame_score != null && <p className="text-muted-foreground">🔥 {s.flame_score}</p>}
                    </div>
                  ))}
                  {userSubmissions && userSubmissions.length > 0 && totalSubmissions > submissionsPageSize && (
                    <DataPagination
                      currentPage={submissionsPage}
                      totalPages={totalSubmissionsPages}
                      pageSize={submissionsPageSize}
                      totalItems={totalSubmissions}
                      onPageChange={setSubmissionsPage}
                      onPageSizeChange={(newSize) => { setSubmissionsPage(1); setSubmissionsPageSize(newSize); }}
                    />
                  )}
                </TabsContent> */}
                {/* <TabsContent value="progress" className="space-y-2">
                  {userProgress?.length === 0 && <p className="text-sm text-muted-foreground">No progress data</p>}
                  {userProgress?.map((p: any, i: number) => (
                    <div key={i} className="border rounded-md p-3 text-sm">
                      {Object.entries(p).filter(([k]) => k !== 'id' && k !== 'user_id').map(([k, v]) => (
                        <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span>{String(v ?? '')}</span></div>
                      ))}
                    </div>
                  ))}
                  {userProgress && userProgress.length > 0 && totalProgress > progressPageSize && (
                    <DataPagination
                      currentPage={progressPage}
                      totalPages={totalProgressPages}
                      pageSize={progressPageSize}
                      totalItems={totalProgress}
                      onPageChange={setProgressPage}
                      onPageSizeChange={(newSize) => { setProgressPage(1); setProgressPageSize(newSize); }}
                    />
                  )}
                </TabsContent> */}
                {/* Gem History — COIN SYSTEM DISABLED
                <TabsContent value="gems" className="space-y-2">
                  {userGemHistory?.length === 0 && <p className="text-sm text-muted-foreground">No gem transactions</p>}
                  {userGemHistory?.map((t: any, i: number) => {
                    const isCredit = t.amount >= 0;
                    const typeColors: Record<string, string> = {
                      bonus: 'bg-blue-100 text-blue-800 border-blue-200',
                      challenge: 'bg-purple-100 text-purple-800 border-purple-200',
                      campaign: 'bg-orange-100 text-orange-800 border-orange-200',
                      milestone: 'bg-green-100 text-green-800 border-green-200',
                      referral: 'bg-pink-100 text-pink-800 border-pink-200',
                      deduction: 'bg-red-100 text-red-800 border-red-200',
                    };
                    const typeColor = typeColors[t.type?.toLowerCase()] ?? 'bg-gray-100 text-gray-700 border-gray-200';

                    // Parse description — tap_milestone:campaignId → "TAP Milestone · campaignId"
                    const rawDesc = t.description || t.notes;
                    let reason: string | null = null;
                    if (rawDesc) {
                      const milestoneMatch = rawDesc.match(/^tap_milestone:(.+)$/);
                      reason = milestoneMatch
                        ? `TAP Milestone · campaign ${milestoneMatch[1]}`
                        : rawDesc;
                    }

                    return (
                      <div key={t.id ?? i} className="border rounded-md p-3 text-sm space-y-2">
                        <div className="flex justify-between items-start">
                          <span className={`font-semibold ${isCredit ? 'text-green-700' : 'text-red-600'}`}>
                            {isCredit ? '+' : ''}{t.amount} gems
                          </span>
                          <span className="text-muted-foreground text-xs">{t.created_at && format(new Date(t.created_at), 'MMM d, yyyy · h:mm a')}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs border ${typeColor}`}>{t.type ?? 'unknown'}</Badge>
                          {reason && <span className="text-muted-foreground text-xs">{reason}</span>}
                        </div>
                      </div>
                    );
                  })}
                  {userGemHistory && userGemHistory?.length > 0  && (
                    <DataPagination
                      currentPage={gemsPage}
                      totalPages={totalGemsPages}
                      pageSize={gemsPageSize}
                      totalItems={totalGems}
                      onPageChange={setGemsPage}
                      onPageSizeChange={(newSize) => { setGemsPage(1); setGemsPageSize(newSize); }}
                    />
                  )}
                </TabsContent>
                */}

                {/* Who changed this account's sign-in email, from what, when
                    and why. Sourced from admin_audit_log, which the DB writes
                    in the same transaction as the change — so this cannot
                    disagree with what actually happened. */}
                <TabsContent value="email" className="space-y-2">
                  <UserEmailHistory userId={selectedUser.id} />
                </TabsContent>

                <TabsContent value="handle" className="space-y-2">
                  <UserEmailHistory userId={selectedUser.id} kind="handle" />
                </TabsContent>
              </Tabs>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function UsersPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Users</h1>
      <Tabs defaultValue="creator" className="w-full">
        <TabsList>
          <TabsTrigger value="creator">Creators</TabsTrigger>
          <TabsTrigger value="coach">Coach creators</TabsTrigger>
          <TabsTrigger value="brand">Solo Brands</TabsTrigger>
          <TabsTrigger value="agency">Agencies(brands)</TabsTrigger>
        </TabsList>
        <TabsContent value="creator">
          <CreatorUsers />
        </TabsContent>
        <TabsContent value="coach">
          <CreatorUsers segment="coach" />
        </TabsContent>
        <TabsContent value="brand">
          <BrandUsers />
        </TabsContent>
        <TabsContent value="agency">
          <AgencyUsers />
        </TabsContent>
      </Tabs>
    </div>
  );
}
